<template>
  <SListItem
    data-cy="classbook-notice-list-item"
    :icon-color="extended ? `text-white ${courseDetails?.color}-dark-bg` : ''"
    :icon-text="extended ? courseDetails?.shortname : ''"
    :badges="badges"
    :title="item.message"
  >
    <template #item-title v-if="!item.message">
      <p class="mb-0 gray-300 small">
        <i>{{ $t('classbook.notices.is_deleted') }} </i>
      </p>
    </template>

    <template #description>
      <p v-if="extended" class="gray-300 nano mb-1">
        {{ $dateFormat.date_time(item.occurred_at) }}
        <span v-if="item.meta && item.meta.description">· {{ item.meta.description }}</span>
      </p>
    </template>

    <template #right-action>
      <classbook-list-item-actions
        v-if="$can('update', item) || $can('delete', item) || $can('view-history', item)"
        :item="item"
        :sr-only-descr="$t('classbook.notices.dropdown.action')"
        @shown="toggleOptionsDropdown(true)"
        @hidden="toggleOptionsDropdown"
        @edit="openEditModal"
        @delete="openDeleteModal"
        @history="openHistoryModal"
      />
    </template>

    <template #modals>
      <!--    add edit notice-->
      <SModal
        v-model="modals.edit"
        scrollable
        :return-focus="`#editDropdown-${item.id}__BV_toggle_`"
        :show-footer-gradient="false"
        :header-title="$t('classbook.notices.edit')"
        data-cy="lesson-details-dc-update-notice-modal"
      >
        <classbook-notice v-if="modals.edit"
                          :entry="entry"
                          :notice="item"
                          @submit="updateNotice"
                          @close="modals.edit = false"
        />
      </SModal>

      <!--    delete confirmation for notice/homework-->
      <SConfirmModal
        v-model="modals.delete"
        :title="$t('classbook.notices.delete.title')"
        :description="$t('classbook.notices.delete.description')"
        :ok-title="$t('classbook.notices.delete.ok_btn')"
        :cancel-title="$t('classbook.notices.delete.cancel_btn')"
        :return-focus="`#editDropdown-${item.id}__BV_toggle_`"
        data-cy="lesson-details-dc-notice-delete-confirmation-modal"
        @ok="deleteItem"
        @hide="modals.delete = false"
      />

      <!--    history of notice -->
      <SModal
        v-model="modals.history"
        data-cy="notice-history"
        :return-focus="`#editDropdown-${item.id}__BV_toggle_`"
        hide-footer
        :header-title="$t('global.history')"
      >
        <classbook-notice-history v-if="modals.history" :notice="item" />
      </SModal>
    </template>
  </SListItem>
</template>

<script>
import ClassbookNotice from 'src/components/classbook/notices/ClassbookNotice.vue'
import ClassbookNoticeHistory from 'src/components/classbook/notices/ClassbookNoticeHistory.vue'
import ClassbookListItemActions from 'src/components/classbook/ClassbookListItemActions.vue'
import Classbooks from 'src/api/classbooks.js'
import classbookStatusFlagMixin from 'src/mixins/classbook/classbookStatusFlagMixin.js'
import constants from 'src/config/constants.ts'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'ClassbookNoticeListItem',

  components: {
    ClassbookListItemActions,
    ClassbookNoticeHistory,
    ClassbookNotice,
  },

  mixins: [classbookStatusFlagMixin],

  props: {
    entry: {
      type: Object,
      default: () => {},
    },

    item: {
      type: Object,
      default: () => {},
    },

    extended: {
      type: Boolean,
      default: true,
    },
  },

  data: () => ({
    showOptions: false,
    modals: {
      delete: false,
      edit: false,
      history: false,
    },
  }),

  computed: {
    displayStatusFlag() {
      return !!this.item.meta?.last_updated_at
    },

    displayUserCount() {
      return this.item.meta?.users_count - this.item.users.length
    },

    getCategoryName() {
      return this.item.category && this.item.category.name
    },

    courseDetails() {
      return this.item.course?.meta
    },

    badges() {
      const badges = [
        {
          text: this.getCategoryName,
          color: 'gray-300',
          testId: 'notice-category-badge',
        },
      ]

      this.item.users.forEach((user, index) => {
        badges.push({
          text: `${user.meta.displayname} ${!!this.displayUserCount && index + 1 === this.item.users.length ? `+ ${this.displayUserCount}` : ''}`,
          color: 'gray',
          testId: 'notice-badge-user',
        })
      })

      if (this.displayStatusFlag) {
        const { updated } = constants.classbook.absences.status
        badges.push(
          this.getClassbookStatusBadge(this.item.deleted_at ? this.$t('classbook.homework.deleted') : this.$t('global.edited'), updated),
        )
      }

      return badges
    },
  },

  emits: ['update', 'delete', 'toggleOptions'],

  methods: {
    toggleOptionsDropdown(open = false) {
      this.showOptions = open
      this.$emit('toggleOptions', open)
    },

    async deleteItem() {
      // TODO - make dynamic to work on homeworks as well
      try {
        await Classbooks.deleteNoticesById(this.item.classbook_id, this.item.id)
        clyTracking.trackClassbook('notice').end.delete()
        this.$emit('delete', this.item.id)
        this.modals.delete = false
      } catch (e) {
        clyTracking.trackClassbook('notice', 'FAILED', e.message).end.delete()
        throw e
      }
    },

    updateNotice(item) {
      this.$emit('update', item)
      this.modals.edit = false
    },

    openEditModal() {
      clyTracking.trackClassbook('notice').start.edit()
      this.modals.edit = true
    },

    openDeleteModal() {
      clyTracking.trackClassbook('notice').start.delete()
      this.modals.delete = true
    },

    openHistoryModal() {
      clyTracking.trackClassbook('notice').interaction.changeHistory()
      this.modals.history = true
    },
  },

  watch: {
    // if user comes back from DC courseMembership we need to open the notice modal again (editing notice)
    '$route.query.showNoticeUsersModal': {
      handler: function (showNoticeUsersEditModal) {
        // Here we can only handle the editing of notices. Creation is handled in the respective
        // components because the creation modal is implemented there.

        // Return in case of
        // - query param is set to false -> !showNoticeUsersCreateModal
        // - modal is already being displayed -> this.modals.edit
        // - the current listItem is not the one from query param -> this.$route.query.noticeId !== String(this.item.id)
        if (!showNoticeUsersEditModal || this.modals.edit || this.$route.query.noticeId !== String(this.item.id)) return
        this.modals.edit = true
      },

      immediate: true,
    },
  },

  created() {},
}
</script>
